import { ActionIcon, Button, Menu, Tooltip } from '@mantine/core';
import { Icon, Select, sizes, variants, type SymbolCodepoints } from '@uag/react-core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { InputPresetId } from 'app/modules/wallforming/components/input-binding/types/inputPresetId';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { selectActiveInputProfile } from 'app/modules/wallforming/components/state-management/placement/slices/inputBindingSlice';

import type { TranslationKey } from 'app/modules/wallforming/components/input-binding/types/translationKey';

import 'app/modules/wallforming/custom-styles/customSelectStyle.css';

const ProfileActionIcon = (props: {
    icon: SymbolCodepoints;
    labelKey: TranslationKey;
    disabled?: boolean;
    onClick: () => void;
}) => {
    const { t } = useTranslation();
    return (
        <Tooltip label={t(props.labelKey)} zIndex={20000}>
            <ActionIcon
                className="action-icon-small"
                disabled={props.disabled}
                size="sm"
                variant="subtle"
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick();
                }}
            >
                <Icon icon={props.icon} />
            </ActionIcon>
        </Tooltip>
    );
};

type ProfileEditorMode = 'create' | 'rename';

export const InputBindingProfileBar = () => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [editorMode, setEditorMode] = useState<ProfileEditorMode>('create');
    const [profileName, setProfileName] = useState('');
    const [sourceProfileId, setSourceProfileId] = useState<string>(InputPresetId.Empty);

    const { profiles, activeProfile, request } = usePlacementStore(
        useShallow((state) => ({
            profiles: state.inputProfiles,
            activeProfile: selectActiveInputProfile(state),
            request: state.inputProfileEditorRequest
        }))
    );
    const {
        setActiveInputProfile,
        addInputProfile,
        renameInputProfile,
        removeInputProfile,
        clearInputProfileEditorRequest,
        updateInputProfileEditorName,
        updateInputProfileEditorSource
    } = usePlacementStore.getState();

    const profileOptions = useMemo(
        () => profiles.map((profile) => ({ value: profile.id, label: profile.name })),
        [profiles]
    );
    const sourceOptions = useMemo(
        () => [{ value: InputPresetId.Empty, label: t('labelKeyBindingProfileEmpty') }, ...profileOptions],
        [profileOptions, t]
    );

    const menuOpen = isMenuOpen || request !== null;
    const currentEditorMode = request?.mode ?? editorMode;
    const currentProfileName = request?.name ?? profileName;
    const currentSourceProfileId = request?.sourceId ?? sourceProfileId;

    const openCreateMenu = () => {
        setEditorMode('create');
        setProfileName('');
        setSourceProfileId(activeProfile.id);
        setIsMenuOpen(true);
    };

    const openRenameMenu = () => {
        setEditorMode('rename');
        setProfileName(activeProfile.name);
        setSourceProfileId(activeProfile.basedOn);
        setIsMenuOpen(true);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        if (request) {
            clearInputProfileEditorRequest();
        }
    };
    const saveProfile = () => {
        if (request) {
            addInputProfile(currentProfileName, request.sourceId, request.pendingBinding);
            clearInputProfileEditorRequest();
        } else if (currentEditorMode === 'rename') {
            renameInputProfile(activeProfile.id, profileName);
        } else {
            addInputProfile(profileName, sourceProfileId);
        }
        closeMenu();
    };
    const handleMenuChange = (open: boolean) => {
        setIsMenuOpen(open);
        if (!open && request) {
            clearInputProfileEditorRequest();
        }
    };

    return (
        <div className="flex items-center gap-1 border-b border-border-ultra-light px-3 py-1.5">
            <span className="shrink-0 text-sm font-medium">{t('labelKeyBindingProfile')}:</span>
            <div className="min-w-0 flex-1">
                <Select
                    className="custom-select profile-select w-full"
                    classNames={{ dropdown: 'profile-dropdown' }}
                    comboboxProps={{ zIndex: 20000 }}
                    data={profileOptions}
                    size="xs"
                    value={activeProfile.id}
                    onChange={(value) => value && setActiveInputProfile(value)}
                />
            </div>
            <div className="ml-auto flex items-center gap-1">
                <Menu
                    opened={menuOpen}
                    position="bottom-end"
                    shadow="md"
                    width={260}
                    zIndex={20000}
                    onChange={handleMenuChange}
                >
                    <Menu.Target>
                        <div className="flex items-center gap-1">
                            <ProfileActionIcon
                                disabled={activeProfile.readOnly}
                                icon="edit"
                                labelKey="labelKeyBindingRenameProfile"
                                onClick={openRenameMenu}
                            />
                            <ProfileActionIcon
                                icon="add"
                                labelKey="labelKeyBindingAddProfile"
                                onClick={openCreateMenu}
                            />
                        </div>
                    </Menu.Target>
                    <Menu.Dropdown className="p-3">
                        <Menu.Label className="min-w-60">
                            {t(
                                currentEditorMode === 'rename'
                                    ? 'labelKeyBindingEditProfile'
                                    : 'labelKeyBindingCreateProfile'
                            )}
                        </Menu.Label>
                        <Menu.Divider />
                        <label className="mt-3 flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-text-light">
                            {t('labelKeyBindingProfileName')}
                            <input
                                className="h-7 w-full rounded border border-border-default bg-background-default px-2 text-xs normal-case outline-none focus:border-border-accent"
                                placeholder={currentProfileName || t('labelKeyBindingNewProfileName')}
                                value={currentProfileName}
                                autoFocus
                                onChange={(e) =>
                                    request
                                        ? updateInputProfileEditorName(e.target.value)
                                        : setProfileName(e.target.value)
                                }
                                onKeyDown={(e) => e.key === 'Enter' && saveProfile()}
                            />
                        </label>
                        <label className="mt-2.5 flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-text-light">
                            {t('labelKeyBindingBasedOn')}
                            <Select
                                className={`custom-select base-profile-select ${
                                    currentEditorMode === 'rename' ? 'cursor-not-allowed' : ''
                                }`}
                                classNames={{ dropdown: 'base-profile-dropdown' }}
                                comboboxProps={{ zIndex: 20000, withinPortal: false }}
                                data={sourceOptions}
                                readOnly={currentEditorMode === 'rename'}
                                size="xs"
                                styles={{
                                    input:
                                        currentEditorMode === 'rename'
                                            ? { cursor: 'not-allowed', opacity: 1 }
                                            : undefined
                                }}
                                value={currentSourceProfileId}
                                onChange={(value) =>
                                    value &&
                                    (request ? updateInputProfileEditorSource(value) : setSourceProfileId(value))
                                }
                            />
                        </label>
                        <div className="mt-3.5 flex justify-end gap-2">
                            <Button size={sizes.button.small} variant={variants.button.secondary} onClick={closeMenu}>
                                {t('cancel')}
                            </Button>
                            <Button
                                disabled={!currentProfileName.trim()}
                                size={sizes.button.small}
                                onClick={saveProfile}
                            >
                                {t(currentEditorMode === 'rename' ? 'rename' : 'create')}
                            </Button>
                        </div>
                    </Menu.Dropdown>
                </Menu>
                <ProfileActionIcon
                    disabled={activeProfile.builtIn}
                    icon="delete"
                    labelKey="labelKeyBindingDeleteProfile"
                    onClick={() => removeInputProfile(activeProfile.id)}
                />
            </div>
        </div>
    );
};
